const BUSINESS_MODULE = require("./businessModules.cjs");
const { APPOINTMENT, PAYMENT, numericStatus } = require("./photographyStatus.cjs");
const asArray = (value) => Array.isArray(value) ? value : [];
const asText = (value) => value === null || value === undefined ? "" : String(value);
const asId = (value) => asText(value);
const asMoney = (value) => {
    const amount = Number(value);
    return Number.isFinite(amount) && amount >= 0 ? amount.toFixed(2) : null;
};
const asSignedMoney = (value) => {
    const amount = Number(value);
    return Number.isFinite(amount) ? amount.toFixed(2) : null;
};

const PHOTOGRAPHY_BOOKING_STATUS = Object.freeze({
    IDLE: "idle",
    LOADING: "loading",
    READY: "ready",
    QUOTING: "quoting",
    QUALIFICATION_REQUIRED: "qualificationRequired",
    SESSION_EXPIRED: "sessionExpired",
    ERROR: "error",
});

const PHOTOGRAPHY_APPOINTMENT_STATUS = Object.freeze({
    IDLE: "idle",
    SUBMITTING: "submitting",
    SUCCEEDED: "succeeded",
    PRICE_CHANGED: "priceChanged",
    RECOVERABLE: "recoverable",
    SESSION_EXPIRED: "sessionExpired",
    ERROR: "error",
});

const getPackagePeopleRange = (pkg) => {
    if (!pkg) return null;
    const min = Number(pkg.minPeople), max = Number(pkg.maxPeople);
    return Number.isInteger(min) && min >= 1 && Number.isInteger(max) && max >= min ? { min, max } : null;
};
const isPackagePeopleCountValid = (pkg, count) => {
    const range = getPackagePeopleRange(pkg), peopleCount = Number(count);
    return !!range && Number.isInteger(peopleCount) && peopleCount >= range.min && peopleCount <= range.max;
};
const clampPackagePeopleCount = (pkg, count) => {
    const range = getPackagePeopleRange(pkg), peopleCount = Number(count);
    return range ? Number.isInteger(peopleCount) ? Math.max(range.min, Math.min(range.max, peopleCount)) : range.min : null;
};

const normalizePackage = (item = {}) => ({
    versionId: asId(item.versionId),
    name: asText(item.name),
    amount: asMoney(item.amount),
    campusAmount: item.campusAmount === null || item.campusAmount === undefined
        ? null : asMoney(item.campusAmount),
    minPeople: Number(item.minPeople),
    maxPeople: Number(item.maxPeople),
    standard: item.standard === true,
});

const normalizePhotographyServiceItem = (item = {}) => {
    const showcaseImageUrls = asArray(item.showcaseImageUrls).filter(Boolean);
    const detailImageUrls = asArray(item.detailImageUrls).filter(Boolean);
    return {
        serviceItemId: asId(item.serviceItemId),
        themeTypeName: asText(item.themeTypeName),
        themeName: asText(item.themeName),
        styleName: asText(item.styleName),
        studioRecruitmentPending: item.studioRecruitmentPending === true,
        campusQualificationRequired: item.campusQualificationRequired === true,
        makeupAmount: asMoney(item.makeupAmount),
        extraPersonAmount: asMoney(item.extraPersonAmount),
        showcaseImageUrls,
        detailImageUrls,
        coverImageUrl: showcaseImageUrls[0] || detailImageUrls[0] || "",
        packages: asArray(item.packages).map(normalizePackage),
    };
};

const createPhotographyBreadcrumb = (serviceItem = {}) => [
    { id: "photography", path: "/photograph", path_name: "影像服务" },
    ...[
        ["theme-type", serviceItem.themeTypeName],
        ["theme", serviceItem.themeName],
        ["style", serviceItem.styleName],
    ].filter(([, name]) => asText(name)).map(([id, name]) => ({
        id,
        path_name: asText(name),
    })),
];

const normalizePhotographyVenues = (items) => asArray(items).map((item = {}) => ({
    id: asId(item.id),
    name: asText(item.name),
    locationSummary: asText(item.locationSummary),
    amount: asMoney(item.amount),
    provinceCode: asId(item.provinceCode),
    provinceName: asText(item.provinceName),
    cityCode: asId(item.cityCode),
    cityName: asText(item.cityName),
    districtCode: asId(item.districtCode),
    districtName: asText(item.districtName),
    venueType: Number(item.venueType),
    venueTypeName: asText(item.venueTypeName),
    campusQualificationRequired: item.campusQualificationRequired === true,
    points: asArray(item.points).filter((point) => point && point.enabled !== false).map((point) => ({
        id: asId(point.id),
        name: asText(point.name),
    })),
}));

const findOrCreateVenueTreeNode = (nodes, value, label) => {
    let node = nodes.find((item) => item.value === value);
    if (!node) {
        node = { value, label, children: [] };
        nodes.push(node);
    }
    return node;
};

const buildPhotographyVenueTree = (venues) => normalizePhotographyVenues(venues).reduce((tree, venue) => {
    const province = findOrCreateVenueTreeNode(tree, `province:${venue.provinceCode}`, venue.provinceName);
    let parent;
    if (venue.venueType === 1) {
        parent = findOrCreateVenueTreeNode(province.children, 'campus', '校园');
        province.children = [parent, ...province.children.filter((node) => node !== parent)];
    } else {
        const city = findOrCreateVenueTreeNode(province.children, `city:${venue.cityCode}`, venue.cityName);
        parent = findOrCreateVenueTreeNode(city.children, `district:${venue.districtCode}`, venue.districtName);
    }
    const venueNode = {
        value: `venue:${venue.id}`,
        label: venue.name,
        venueType: venue.venueType,
        campus: venue.campusQualificationRequired,
    };
    if (venue.points.length) {
        venueNode.children = venue.points.map((point) => ({ value: `point:${point.id}`, label: point.name }));
    }
    parent.children.push(venueNode);
    return tree;
}, []);

const venuePickerSnapshot = (state) => ({
    leftNodes: state.leftNodes,
    rightNodes: state.rightNodes,
    activeLeftValue: state.activeLeftValue,
    prefixTokens: state.prefixTokens,
    depth: state.depth,
});

const createPhotographyVenuePickerState = (venues) => {
    const leftNodes = buildPhotographyVenueTree(venues);
    const activeLeft = leftNodes[0] || null;
    return {
        leftNodes,
        rightNodes: activeLeft && Array.isArray(activeLeft.children) ? activeLeft.children : [],
        activeLeftValue: activeLeft ? activeLeft.value : "",
        prefixTokens: [],
        depth: 0,
        history: [],
        selection: null,
    };
};

const selectPhotographyVenuePickerLeft = (state, value) => {
    const activeLeft = (state.leftNodes || []).find((item) => item.value === value);
    if (!activeLeft) return state;
    return {
        ...state,
        activeLeftValue: activeLeft.value,
        rightNodes: Array.isArray(activeLeft.children) ? activeLeft.children : [],
        selection: activeLeft.value.startsWith('venue:') && !(activeLeft.children || []).length
            ? [...(state.prefixTokens || []), activeLeft.value] : null,
    };
};

const advancePhotographyVenuePicker = (state, value) => {
    const selectedRight = (state.rightNodes || []).find((item) => item.value === value);
    if (!selectedRight) return state;
    const path = [...(state.prefixTokens || []), state.activeLeftValue, selectedRight.value].filter(Boolean);
    if (!Array.isArray(selectedRight.children) || !selectedRight.children.length) {
        return { ...state, selection: path };
    }
    return {
        leftNodes: state.rightNodes,
        rightNodes: selectedRight.children,
        activeLeftValue: selectedRight.value,
        prefixTokens: [...(state.prefixTokens || []), state.activeLeftValue].filter(Boolean),
        depth: Number(state.depth) + 1,
        history: [...(state.history || []), venuePickerSnapshot(state)],
        selection: null,
    };
};

const retreatPhotographyVenuePicker = (state) => {
    const history = state.history || [];
    if (!history.length) return state;
    return {
        ...history[history.length - 1],
        history: history.slice(0, -1),
        selection: null,
    };
};

const createPhotographyVenueAddress = (venue = {}, point = null) => {
    const regionParts = [venue.provinceName, venue.cityName, venue.districtName].map(asText).filter(Boolean);
    const structuredLocationText = [...regionParts, asText(venue.name)].join("");
    const locationSummary = asText(venue.locationSummary).trim();
    const summaryParts = locationSummary.split(/[\s·/]+/).filter(Boolean);
    const summaryRepeatsStructuredLocation = summaryParts.length > 0
        && summaryParts.every((part) => structuredLocationText.includes(part));
    return [
        ...regionParts,
        ...(!locationSummary || summaryRepeatsStructuredLocation ? [] : [locationSummary]),
        asText(venue.name),
        point ? asText(point.name) : "",
    ].filter(Boolean).join(" / ");
};

const resolvePhotographyVenueSelection = (selection, venues) => {
    const tokens = asArray(selection).map(asText);
    const venueToken = tokens.find((token) => token.startsWith("venue:"));
    const pointToken = tokens.find((token) => token.startsWith("point:"));
    const venue = normalizePhotographyVenues(venues).find((item) => `venue:${item.id}` === venueToken);
    if (!venue) return null;
    const point = pointToken
        ? venue.points.find((item) => `point:${item.id}` === pointToken)
        : null;
    if ((venue.points.length && !point) || (!venue.points.length && pointToken)) return null;
    const expectedLastToken = point ? `point:${point.id}` : `venue:${venue.id}`;
    if (tokens[tokens.length - 1] !== expectedLastToken) return null;
    return {
        venueId: venue.id,
        pointId: point ? point.id : null,
        venueName: venue.name,
        pointName: point ? point.name : "",
    };
};

const createPhotographyVenueSelectionPath = (venueId, pointId, venues) => {
    const venue = normalizePhotographyVenues(venues).find((item) => item.id === asId(venueId));
    if (!venue) return [];
    const point = pointId === null || pointId === undefined || pointId === ""
        ? null
        : venue.points.find((item) => item.id === asId(pointId));
    if ((venue.points.length && !point) || (!venue.points.length && point)) return [];
    return [
        `province:${venue.provinceCode}`,
        ...(venue.venueType === 1 ? ['campus'] : [`city:${venue.cityCode}`, `district:${venue.districtCode}`]),
        `venue:${venue.id}`,
        ...(point ? [`point:${point.id}`] : []),
    ];
};

const getPhotographyBookingDraftError = (draft = {}, booking = {}) => {
    const serviceItem = booking.serviceItem || {};
    if (!serviceItem.serviceItemId || asId(draft.serviceItemId) !== asId(serviceItem.serviceItemId)) {
        return "服务项目已失效，请返回摄影详情页重新选择。";
    }
    if (!asArray(serviceItem.packages).some((item) => asId(item.versionId) === asId(draft.packageVersionId))) {
        return "所选套餐已失效，请返回摄影详情页重新选择。";
    }
    const pkg = asArray(serviceItem.packages).find(item => asId(item.versionId) === asId(draft.packageVersionId));
    if (!isPackagePeopleCountValid(pkg, draft.peopleCount)) return "人数不符合套餐范围，请返回摄影详情页重新选择。";
    const venue = normalizePhotographyVenues(booking.venues).find((item) => item.id === asId(draft.venueId));
    if (!venue) return "所选场地已失效，请返回摄影详情页重新选择。";
    const pointId = asId(draft.pointId);
    if (venue.points.length && !pointId) return "该场地必须选择具体点位，请返回摄影详情页重新选择。";
    if (pointId && !venue.points.some((point) => point.id === pointId)) {
        return "所选场地点位已失效，请返回摄影详情页重新选择。";
    }
    return "";
};

const normalizePhotographyQuote = (item = {}) => {
    const quote = {
        serviceItemId: asId(item.serviceItemId),
        themeTypeId: asId(item.themeTypeId),
        themeId: asId(item.themeId),
        styleId: asId(item.styleId),
        packageId: asId(item.packageId),
        packageVersionId: asId(item.packageVersionId),
        venueId: asId(item.venueId),
        pointId: asId(item.pointId) || null,
        themeTypeName: asText(item.themeTypeName),
        themeName: asText(item.themeName),
        styleName: asText(item.styleName),
        packageName: asText(item.packageName),
        venueName: asText(item.venueName),
        pointName: asText(item.pointName),
        venueLocationSummary: asText(item.venueLocationSummary),
        peopleCount: Number(item.peopleCount) || 1,
        makeupSelected: item.makeupSelected === true,
        venueType: Number(item.venueType),
        venueTypeName: asText(item.venueTypeName),
        campusQualificationRequired: item.campusQualificationRequired === true,
        campusQualificationSatisfied: item.campusQualificationSatisfied === true,
        packageAmount: asMoney(item.packageAmount),
        regularPackageAmount: asMoney(item.regularPackageAmount),
        campusPackageAmount: item.campusPackageAmount === null || item.campusPackageAmount === undefined
            ? null : asMoney(item.campusPackageAmount),
        adoptedPackageAmount: asMoney(item.adoptedPackageAmount),
        packagePriceDifference: asSignedMoney(item.packagePriceDifference),
        extraPeopleAmount: asMoney(item.extraPeopleAmount),
        photographyAmount: asMoney(item.photographyAmount),
        makeupAmount: asMoney(item.makeupAmount),
        venueAmount: asMoney(item.venueAmount),
        regularTotalAmount: asMoney(item.regularTotalAmount === null || item.regularTotalAmount === undefined
            ? Number(item.totalAmount) - Number(item.packagePriceDifference)
            : item.regularTotalAmount),
        userCouponId: item.userCouponId == null ? null : Number(item.userCouponId),
        discountAmount: asMoney(item.discountAmount),
        payableAmount: asMoney(item.payableAmount == null && item.userCouponId == null ? item.totalAmount : item.payableAmount),
        totalAmount: asMoney(item.totalAmount),
    };
    quote.breakdown = [
        { key: "photography", label: "摄影服务", amount: quote.photographyAmount },
        { key: "makeup", label: "化妆服务", amount: quote.makeupAmount },
        { key: "venue", label: "场地费用", amount: quote.venueAmount },
    ];
    return quote;
};

const getCampusPricePresentation = (quote = {}, campusVenue = false) => ({
    promotional: campusVenue === true && Number(quote.packagePriceDifference) < 0,
    adoptedAmount: asMoney(quote.adoptedPackageAmount || quote.packageAmount),
    regularAmount: asMoney(quote.regularTotalAmount === null || quote.regularTotalAmount === undefined
        ? Number(quote.totalAmount) - Number(quote.packagePriceDifference)
        : quote.regularTotalAmount),
    regularTotalAmount: asMoney(quote.regularTotalAmount === null || quote.regularTotalAmount === undefined
        ? Number(quote.totalAmount) - Number(quote.packagePriceDifference)
        : quote.regularTotalAmount),
});

const createQuotePayload = (draft = {}) => {
    const venueId = draft.venueId === null || draft.venueId === undefined || draft.venueId === ""
        ? null : Number(draft.venueId);
    return {
        ...(draft.userCouponId != null ? { userCouponId: Number(draft.userCouponId) } : {}),
        serviceItemId: Number(draft.serviceItemId),
        packageVersionId: Number(draft.packageVersionId),
        venueId,
        pointId: draft.pointId === null || draft.pointId === undefined || draft.pointId === "" ? null : Number(draft.pointId),
        peopleCount: Number(draft.peopleCount),
        makeupSelected: draft.makeupSelected === true,
    };
};

const normalizeProfessionalIdentityCode = (value) => asText(value).trim().toUpperCase();
const isProfessionalIdentityCode = (value) => /^[A-Z0-9]{8}$/.test(normalizeProfessionalIdentityCode(value));

const createAppointmentConfirmationQuery = (draft = {}) => {
    const query = {
        serviceItemId: asId(draft.serviceItemId),
        packageVersionId: asId(draft.packageVersionId),
        venueId: asId(draft.venueId),
        peopleCount: asText(Number(draft.peopleCount)),
        makeupSelected: draft.makeupSelected === true ? "1" : "0",
        shootAt: asText(draft.shootAt),
    };
    const pointId = asId(draft.pointId);
    if (pointId) query.pointId = pointId;
    const photographerIdentityCode = normalizeProfessionalIdentityCode(draft.photographerIdentityCode);
    const makeupArtistIdentityCode = normalizeProfessionalIdentityCode(draft.makeupArtistIdentityCode);
    if (photographerIdentityCode) query.photographerIdentityCode = photographerIdentityCode;
    if (query.makeupSelected === "1" && makeupArtistIdentityCode) query.makeupArtistIdentityCode = makeupArtistIdentityCode;
    return query;
};
const normalizeChinaMainlandPhone = (value) => asText(value).trim();
const isChinaMainlandPhone = (value) => /^1[3-9]\d{9}$/.test(normalizeChinaMainlandPhone(value));
const maskChinaMainlandPhone = (value) => {
    const phone = normalizeChinaMainlandPhone(value);
    return isChinaMainlandPhone(phone) ? `${phone.slice(0, 3)}****${phone.slice(-4)}` : "当前号码";
};

const parseAppointmentConfirmationDraft = (query = {}) => {
    const serviceItemId = asId(query.serviceItemId);
    const packageVersionId = asId(query.packageVersionId);
    const venueId = asId(query.venueId);
    const pointId = asId(query.pointId);
    const peopleCount = Number(query.peopleCount);
    const makeupSelected = asText(query.makeupSelected) === "1";
    const shootAt = asText(query.shootAt);
    const photographerIdentityCode = normalizeProfessionalIdentityCode(query.photographerIdentityCode);
    const makeupArtistIdentityCode = normalizeProfessionalIdentityCode(query.makeupArtistIdentityCode);
    if (!/^\d+$/.test(serviceItemId) || !/^\d+$/.test(packageVersionId) || !/^\d+$/.test(venueId) || (pointId && !/^\d+$/.test(pointId))
        || !Number.isInteger(peopleCount) || peopleCount < 1 || !parseShanghaiDateTime(shootAt)
        || (photographerIdentityCode && !isProfessionalIdentityCode(photographerIdentityCode))
        || (makeupArtistIdentityCode && !isProfessionalIdentityCode(makeupArtistIdentityCode))) return null;
    return {
        serviceItemId,
        packageVersionId,
        venueId,
        pointId: pointId || null,
        peopleCount,
        makeupSelected,
        photographerIdentityCode,
        makeupArtistIdentityCode: makeupSelected ? makeupArtistIdentityCode : "",
        shootAt,
    };
};

const createProfessionalIdentityPrevalidationPayload = (draft = {}) => {
    const payload = {
        professionalIdentityCode: normalizeProfessionalIdentityCode(draft.professionalIdentityCode),
        expectedProfessionalType: asText(draft.expectedProfessionalType),
        serviceItemId: Number(draft.serviceItemId),
        venueId: Number(draft.venueId),
    };
    const shootAt = asText(draft.shootAt);
    if (shootAt) payload.shootAt = shootAt;
    return payload;
};

const normalizeDirectedCreator = (item = {}) => ({
    professionalIdentityCode: normalizeProfessionalIdentityCode(item.professionalIdentityCode),
    portrait: {
        accessUrl: asText(item.portrait && item.portrait.accessUrl),
        expiresAt: asText(item.portrait && item.portrait.expiresAt),
    },
    realNameMasked: asText(item.realNameMasked),
    eligibleForInvitation: item.eligibleForInvitation === true,
});

const isDirectedCreatorPortraitFresh = (creator, now = new Date()) => {
    const portrait = creator && creator.portrait;
    const expiresAt = parseShanghaiDateTime(portrait && portrait.expiresAt);
    return !!(portrait && asText(portrait.accessUrl) && expiresAt && expiresAt.getTime() > now.getTime());
};

const createAppointmentPayload = (draft = {}, quote = {}, idempotencyKey, paymentChannel = "") => {
    const payload = {
        idempotencyKey: asText(idempotencyKey),
        serviceItemId: Number(draft.serviceItemId),
        packageVersionId: Number(draft.packageVersionId),
        venueId: Number(draft.venueId),
        pointId: draft.pointId === null || draft.pointId === undefined || draft.pointId === "" ? null : Number(draft.pointId),
        peopleCount: Number(draft.peopleCount),
        makeupSelected: draft.makeupSelected === true,
        contactPhone: normalizeChinaMainlandPhone(draft.contactPhone),
        contactSmsCode: asText(draft.contactSmsCode),
        shootAt: asText(draft.shootAt),
        confirmedTotalAmount: asMoney(quote.totalAmount),
    };
    if (draft.confirmedPayableAmount != null) payload.confirmedPayableAmount = asMoney(draft.confirmedPayableAmount);
    if (draft.userCouponId != null) payload.userCouponId = Number(draft.userCouponId);
    const photographerIdentityCode = normalizeProfessionalIdentityCode(draft.photographerIdentityCode);
    const makeupArtistIdentityCode = normalizeProfessionalIdentityCode(draft.makeupArtistIdentityCode);
    if (photographerIdentityCode) payload.photographerIdentityCode = photographerIdentityCode;
    if (payload.makeupSelected && makeupArtistIdentityCode) payload.makeupArtistIdentityCode = makeupArtistIdentityCode;
    return payload;
};

const createIntentFingerprint = (draft, quote) => JSON.stringify(createAppointmentPayload(
    draft,
    quote,
    "",
    draft && draft.paymentChannel,
));
const defaultIntentKeyFactory = () => `photography-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

const createBookingIntent = (currentIntent, draft, quote, keyFactory = defaultIntentKeyFactory) => {
    const fingerprint = createIntentFingerprint(draft, quote);
    if (currentIntent && currentIntent.fingerprint === fingerprint) return currentIntent;
    return { fingerprint, idempotencyKey: asText(keyFactory()).slice(0, 100) };
};

const normalizePhotographyAppointment = (item = {}) => {
    const currentPaymentRequest = item.currentPaymentRequest || {};
    const paymentStatus = numericStatus(item.paymentStatus == null ? currentPaymentRequest.paymentStatus : item.paymentStatus, PAYMENT);
    return {
        appointmentNo: asText(item.appointmentNo),
        appointmentStatus: numericStatus(item.appointmentStatus == null ? (item.status == null && paymentStatus === PAYMENT.CREATE_UNKNOWN ? APPOINTMENT.WAITING_PAYMENT : item.status) : item.appointmentStatus, APPOINTMENT),
        paymentDeadline: asText(item.paymentDeadline || item.expiresAt || currentPaymentRequest.expiresAt),
        totalAmount: asMoney(item.totalAmount),
        payableAmount: asMoney(item.payableAmount === undefined ? item.totalAmount : item.payableAmount),
        paymentRequestNo: asText(item.paymentRequestNo || currentPaymentRequest.paymentRequestNo),
        paymentStatus,
        paymentChannel: asText(item.channel || currentPaymentRequest.channel),
    };
};

const getAppointmentErrorDetails = (error = {}) => {
    const response = error.response || {};
    const payload = response.data || {};
    const code = Number(response.status || payload.code || error.code);
    const message = asText(payload.message || error.message || "摄影预约创建失败，请稍后重试");
    return { code, message };
};

const isAppointmentPriceChangeError = (error) => {
    const { code, message } = getAppointmentErrorDetails(error);
    return code === 409 && message.includes("报价已变化");
};

const getAppointmentFailure = (error = {}, previousQuote = {}, currentQuote = null) => {
    const { code, message } = getAppointmentErrorDetails(error);
    if (isAppointmentPriceChangeError(error) && currentQuote) {
        return {
            kind: PHOTOGRAPHY_APPOINTMENT_STATUS.PRICE_CHANGED,
            message,
            previousQuote: normalizePhotographyQuote(previousQuote),
            currentQuote: normalizePhotographyQuote(currentQuote),
        };
    }
    if (error.code === "ECONNABORTED" || /timeout|超时/i.test(message)) {
        return {
            kind: PHOTOGRAPHY_APPOINTMENT_STATUS.RECOVERABLE,
            message: "请求已超时，预约结果暂时未知。可使用原提交意图恢复创建结果，不会重复创建预约。",
        };
    }
    return {
        kind: code === 401 ? PHOTOGRAPHY_APPOINTMENT_STATUS.SESSION_EXPIRED : PHOTOGRAPHY_APPOINTMENT_STATUS.ERROR,
        message,
    };
};

const parseShanghaiDateTime = (value) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/.exec(asText(value));
    if (!match) return null;
    const parts = match.slice(1).map(Number);
    const [year, month, day, hour, minute, second = 0] = parts;
    const timestamp = Date.UTC(year, month - 1, day, hour - 8, minute, second, 0);
    const shanghaiView = new Date(timestamp + 8 * 60 * 60 * 1000);
    const valid = shanghaiView.getUTCFullYear() === year &&
        shanghaiView.getUTCMonth() === month - 1 &&
        shanghaiView.getUTCDate() === day &&
        shanghaiView.getUTCHours() === hour &&
        shanghaiView.getUTCMinutes() === minute &&
        shanghaiView.getUTCSeconds() === second;
    return valid ? new Date(timestamp) : null;
};

const formatPhotographySuggestedArrival = (shootAt, leadMinutes) => {
    const parsed = parseShanghaiDateTime(shootAt);
    const lead = Number(leadMinutes);
    if (!parsed || !Number.isFinite(lead) || lead < 0) return "待确认";
    const shanghaiView = new Date(parsed.getTime() - lead * 60 * 1000 + 8 * 60 * 60 * 1000);
    return [
        shanghaiView.getUTCFullYear(),
        String(shanghaiView.getUTCMonth() + 1).padStart(2, "0"),
        String(shanghaiView.getUTCDate()).padStart(2, "0"),
    ].join("-") + ` ${String(shanghaiView.getUTCHours()).padStart(2, "0")}:${String(shanghaiView.getUTCMinutes()).padStart(2, "0")}`;
};

const isShootAtAllowed = (shootAt, now = new Date(), hasDirectedTarget = false, makeupSelected = false) => {
    const parsed = parseShanghaiDateTime(shootAt);
    if (!parsed) return false;
    const requiredLeadMilliseconds = hasDirectedTarget
        ? (makeupSelected ? 60 * 60 * 1000 : 0)
        : 24 * 60 * 60 * 1000;
    const threshold = now.getTime() + requiredLeadMilliseconds;
    return hasDirectedTarget ? parsed.getTime() > threshold : parsed.getTime() >= threshold;
};
const isAppointmentContactVerificationError = (error) => getAppointmentErrorDetails(error).code === 400;

const isShootDateDisabled = (date, now = new Date(), hasDirectedTarget = false, makeupSelected = false) => {
    const selected = new Date(date);
    if (Number.isNaN(selected.getTime())) return true;
    const year = selected.getFullYear();
    const month = String(selected.getMonth() + 1).padStart(2, "0");
    const day = String(selected.getDate()).padStart(2, "0");
    const dayEnd = parseShanghaiDateTime(`${year}-${month}-${day} 23:59:59`);
    const minimumTimestamp = now.getTime() + (hasDirectedTarget ? (makeupSelected ? 60 * 60 * 1000 : 0) : 24 * 60 * 60 * 1000);
    return !dayEnd || dayEnd.getTime() + 999 < minimumTimestamp;
};

const getShootTimeMinTime = (shootDate, now = new Date(), hasDirectedTarget = false, makeupSelected = false) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(asText(shootDate))
        || !parseShanghaiDateTime(`${shootDate} 00:00:00`)) return "";
    const cutoff = new Date(now.getTime() + (hasDirectedTarget ? (makeupSelected ? 60 * 60 * 1000 : 0) : 24 * 60 * 60 * 1000));
    const shanghaiCutoff = new Date(cutoff.getTime() + 8 * 60 * 60 * 1000);
    const cutoffDate = [
        shanghaiCutoff.getUTCFullYear(),
        String(shanghaiCutoff.getUTCMonth() + 1).padStart(2, "0"),
        String(shanghaiCutoff.getUTCDate()).padStart(2, "0"),
    ].join("-");
    if (shootDate < cutoffDate) return "23:59";
    if (shootDate > cutoffDate) return "";
    let cutoffMinutes = shanghaiCutoff.getUTCHours() * 60 + shanghaiCutoff.getUTCMinutes();
    if (!hasDirectedTarget && shanghaiCutoff.getUTCSeconds() === 0 && shanghaiCutoff.getUTCMilliseconds() === 0) {
        if (cutoffMinutes === 0) return "";
        cutoffMinutes -= 1;
    }
    return `${String(Math.floor(cutoffMinutes / 60)).padStart(2, "0")}:${String(cutoffMinutes % 60).padStart(2, "0")}`;
};

const getCampusBookingGate = (required, status) => {
    if (!required || status === "APPROVED") return { blocked: false, reason: "" };
    const reasons = {
        PENDING: "校园认证正在审核中，审核通过后可提交校园主题预约。",
        EXPIRED: "校园认证已过期，重新完成认证后可提交校园主题预约。",
        SUSPENDED: "校园认证已暂停，恢复有效状态后可提交校园主题预约。",
        REVOKED: "校园认证已失效，重新完成认证后可提交校园主题预约。",
    };
    return { blocked: true, reason: reasons[status] || "请先完成有效校园认证，再提交校园场地预约。" };
};

const resolveCampusBookingGate = ({ required, campusStatus, quoteStatus, hasQuote, quoteError, quote }) => {
    if (quoteStatus === PHOTOGRAPHY_BOOKING_STATUS.QUALIFICATION_REQUIRED) {
        return { blocked: true, reason: quoteError || "校园主题需完成有效认证后才能提交预约。" };
    }
    return getCampusBookingGate(required, campusStatus);
};

const getQuoteFailureStatus = (error = {}) => {
    const response = error.response || {};
    const code = Number(response.status || (response.data && response.data.code) || error.code);
    if (code === 401) return PHOTOGRAPHY_BOOKING_STATUS.SESSION_EXPIRED;
    if (code === 403) return PHOTOGRAPHY_BOOKING_STATUS.QUALIFICATION_REQUIRED;
    return PHOTOGRAPHY_BOOKING_STATUS.ERROR;
};

module.exports = {
    getPackagePeopleRange, isPackagePeopleCountValid, clampPackagePeopleCount,
    BUSINESS_MODULE,
    PHOTOGRAPHY_APPOINTMENT_STATUS,
    PHOTOGRAPHY_BOOKING_STATUS,
    advancePhotographyVenuePicker,
    buildPhotographyVenueTree,
    createPhotographyVenuePickerState,
    createAppointmentConfirmationQuery,
    createAppointmentPayload,
    createBookingIntent,
    createPhotographyBreadcrumb,
    createPhotographyVenueAddress,
    createPhotographyVenueSelectionPath,
    createProfessionalIdentityPrevalidationPayload,
    createQuotePayload,
    formatPhotographySuggestedArrival,
    getAppointmentFailure,
    getCampusBookingGate,
    getCampusPricePresentation,
    getPhotographyBookingDraftError,
    getQuoteFailureStatus,
    getShootTimeMinTime,
    isShootDateDisabled,
    isShootAtAllowed,
    isDirectedCreatorPortraitFresh,
    isAppointmentPriceChangeError,
    isAppointmentContactVerificationError,
    isChinaMainlandPhone,
    isProfessionalIdentityCode,
    normalizeDirectedCreator,
    maskChinaMainlandPhone,
    normalizePhotographyAppointment,
    normalizePhotographyQuote,
    normalizePhotographyServiceItem,
    normalizePhotographyVenues,
    normalizeProfessionalIdentityCode,
    parseAppointmentConfirmationDraft,
    resolveCampusBookingGate,
    resolvePhotographyVenueSelection,
    retreatPhotographyVenuePicker,
    selectPhotographyVenuePickerLeft,
};
